/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
import Vue from 'vue';

import Cookies from '@/cookies';
import { normalizeRelations, resolveRelations } from '@/store/helpers';
import userService from '@/services/user';

const state = {
    access_token: Cookies.read('imp-token') || Cookies.read('user-token') || '',
    byId: {},
    allIds: [],
    preferences: {},
};

const getters = {
    // Authentication
    isAuthenticated: state => !!state.access_token,
    accessToken: state => state.access_token,
    // Return a single article with the given id.
    find: (state, _, __, rootGetters) => id =>
        // Swap ID referenes with the resolved author objects.
        resolveRelations(state.byId[id], ['team'], rootGetters),
    // Return a list of items in the order of `allIds`.
    list: (state, getters) => state.allIds.map(id => getters.find(id)),
    loaded: state => state.allIds.length > 0,
    id: (state, getters) => (getters.loaded ? state.allIds[0] : null),
    current: (state, getters) => getters.find(state.allIds[0]),
    teamId: (state, getters) => getters.current.team[0].id,
    preferences: state => state.preferences || null,
};

const actions = {
    load: async ({ dispatch, commit, getters }) => {
        const items = await userService.getMe();
        if (items.data.relationships.team.data.length === 0) {
            throw new Error('User does not have an account');
        }
        items.data.role = items.data.relationships.team.data[0].meta.role;
        commit('add', normalizeRelations(items.data, ['team']));
        if (typeof items.included !== 'undefined') {
            items.included.forEach((item) => {
                // Add or update the included item.
                commit(`${item.type}/add`, item, { root: true });
            });
        }
        return dispatch('team/load', getters.teamId, { root: true });
    },
    update: async ({ dispatch }, user) => userService.update(user)
        .then(() => dispatch('load')),
    // Authentication
    login: ({ dispatch }, attributes) => axios.post(
        `${process.env.VUE_APP_API_ENDPOINT}/login`,
        {
            data: {
                type: 'login',
                attributes,
            },
        },
    ).then(response => dispatch(
        'setToken',
        { token: response.data.meta.access_token, remember: false },
    )),

    logout: ({ dispatch }) => axios.post(`${process.env.VUE_APP_API_ENDPOINT}/logout`).then(() => dispatch('removeToken'))
        .catch((error) => {
            console.log('Error logging out:', error);
            dispatch('endProcessing', null, { root: true });
            if (error.response.status !== undefined && error.response.status === 401) {
                // Token expired/revoked, so we're good, let's close this session
                return dispatch('removeToken');
            }

            if (typeof Bugsnag !== 'undefined') {
                Bugsnag.notify('Error logging out user', {
                    metaData: error,
                });
            }

            return error;
        }),
    setToken: ({ commit }, credentials) => {
        if (credentials === undefined || credentials.token === undefined || !credentials.token) {
            return Promise.reject(new Error('Error persisting token (empty), check system!'));
        }
        Cookies.create('user-token', credentials.token, credentials.remember ? 30 : null);
        axios.defaults.headers.common.Authorization = `Bearer ${credentials.token}`;
        commit('setToken', credentials.token);
        return Promise.resolve(credentials.token);
    },
    removeToken: ({ dispatch, commit }) => {
        if (Cookies.read('imp-token')) {
            Cookies.erase('imp-token');
        } else {
            Cookies.erase('user-token');
        }
        commit('clearAll');
        // clear team
        dispatch('team/clearAll', null, { root: true }); // clear monitors
    },
    loadPreferences: ({ commit }) => {
        const persisted = Cookies.read('preferences');
        if (persisted) {
            const prefs = JSON.parse(persisted);
            _.forEach(
                prefs,
                (value, name) => commit('setPreference', { name, value }),
            );
            console.log('Loaded preferences', prefs);
        }
    },
    savePreference: ({ commit, state }, preference) => {
        commit('setPreference', preference);
        Cookies.create('preferences', JSON.stringify(state.preferences));
    },
};

const mutations = {
    add: (state, item) => {
        Vue.set(state.byId, item.id, item);
        if (state.allIds.includes(item.id)) return;
        state.allIds.push(item.id);
    },
    setToken: (state, token) => {
        state.access_token = token;
    },
    clearAll: (state) => {
        state.byId = {};
        state.allIds = [];
        state.preferences = {};
        state.access_token = '';
    },
    setPreference: (state, preference) => {
        state.preferences[preference.name] = preference.value;
    },
};

export default {
    actions,
    getters,
    mutations,
    namespaced: true,
    state,
};
